<template>
  <div class="highlight-block">
    <BaseButton
      class="active-category"
      text
      color="white"
      retain-focus-on-click
      :class="isIconDisabled ? 'disabled disabled-icon' : ''"
      :ripple="false"
      :disabled="!isCategoriesExist"
      @touchstart="categoryTouchStart"
      @touchend="resetTimer"
    >
      <div
        class="highlight-element"
        @click="processCreateAnnotationEvent(activeCategory)"
      >
        <div class="marker-container">
          <div
            class="marker"
            :class="getActiveCategoryStyles()"
            :style="{
              'background-color': getBackgroundColor(activeCategory)
            }"
          />
        </div>
        <span class="context-menu-text-item">
          {{ $t('Context.highlight') }}
        </span>
      </div>
      <div
        class="collapse-icon-block close-prevent"
        @click="toggleHighlightOptions"
      >
        <BaseSpriteIcon
          :icon-name="isClosed ? 'ico-arrow-right' : 'ico-arrow-left'"
        />
      </div>
    </BaseButton>
    <div v-if="isCategoriesExist" :class="isClosed ? 'd-none' : 'd-flex'">
      <BaseButton
        v-for="category in categories"
        :key="category.id"
        class="highlight-option"
        :class="isIconDisabled ? 'disabled disabled-icon' : ''"
        text
        color="white"
        retain-focus-on-click
        :ripple="false"
        @click="processCreateAnnotationEvent(category)"
      >
        <div class="highlight-element">
          <div class="marker-container">
            <div
              class="marker"
              :class="[getStyleByAnnCategory(category.id), isActive(category)]"
              :style="{
                'background-color': getBackgroundColor(category)
              }"
            >
              <BaseSpriteIcon icon-name="ico-check" />
            </div>
          </div>
          <span class="context-menu-text-item">
            {{ getCategoryName(category) }}
          </span>
        </div>
      </BaseButton>
    </div>
  </div>
</template>

<script>
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';
import BaseButton from '@/components/base/BaseButton/BaseButton';
import ThemeClassNamesEnum from '@/enums/ThemeClassNamesEnum';

import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('HighlightControl.vue');

export default {
  name: 'HighlightControl',
  components: {
    BaseSpriteIcon,
    BaseButton
  },
  data() {
    return {
      isClosed: true,
      longtouchTimer: null,
      choosenCategory: null
    };
  },
  computed: {
    categories() {
      return this.$store.getters['CategoriesStore/getShownCategories'] || [];
    },
    isCategoriesExist() {
      return this.categories.length;
    },
    activeCategory() {
      return this.categories[0];
    },
    isGuest() {
      return this.$store.getters['UserStore/isGuestUser'];
    },
    isDisabledIconInAttr() {
      return this.$attrs['icon-disabled'];
    },
    isIconDisabled() {
      return (
        this.isDisabledIconInAttr || this.isGuest || !this.isCategoriesExist
      );
    }
  },
  methods: {
    processCreateAnnotationEvent(category) {
      if (!this.isCategoriesExist) {
        return;
      }
      if (this.isDisabledIconInAttr && !this.isGuest) {
        this.$store.dispatch('ManagePopupStore/openToaster', {
          text: this.$t('AnnotationControl.toaster.disabled.message'),
          toasterClose: false
        });
        return;
      }
      this.choosenCategory = category;
      this.$store.dispatch('PaymentsStore/wrappWithGuestLogic', {
        handler: this.createAnnotation
      });
    },
    resetTimer() {
      if (this.longtouchTimer) {
        clearTimeout(this.longtouchTimer);
        this.longtouchTimer = null;
      }
    },
    categoryTouchStart() {
      this.resetTimer();
      this.longtouchTimer = setTimeout(() => {
        this.longtouchTimer = null;
        this.toggleHighlightOptions();
      }, 500);
    },
    isActive(category) {
      return this.activeCategory.id === category.id ? 'active' : '';
    },
    getActiveCategoryStyles() {
      if (!this.activeCategory?.id) {
        return '';
      }
      return this.getStyleByAnnCategory(this.activeCategory.id);
    },
    getStyleByAnnCategory(categoryId) {
      return this.$store.getters['CategoriesStore/getAnnClassByCategoryId'](
        categoryId
      );
    },
    $_isNightMode() {
      return (
        this.$store.getters['ReadingSettingsStore/getThemeName'] ===
        ThemeClassNamesEnum.NIGHT
      );
    },
    getBackgroundColor(category) {
      if (!this.isCategoriesExist) {
        return AppConstantsUtil.DISABLED_MARK_COLOR;
      }
      return this.$_isNightMode() ? category.nightColor : category.color;
    },
    getCategoryName(category) {
      return this.$te(category.label) ? this.$t(category.label) : category.name;
    },
    toggleHighlight() {
      this.isClosed = !this.isClosed;
    },
    toggleHighlightOptions() {
      if (!this.isCategoriesExist) {
        return;
      }
      this.$store.dispatch('PaymentsStore/wrappWithGuestLogic', {
        handler: this.toggleHighlight
      });
    },
    getSelectedLocator() {
      return this.$store.getters['MaterialsStore/getSelectedLocator'];
    },
    selectedText() {
      const locators = this.getSelectedLocator();
      if (!locators) {
        return null;
      }
      return this.$store.getters['BookStore/getTextWithContextByLocators'](
        locators
      );
    },
    createAnnData() {
      const selectedText = this.selectedText();
      if (!selectedText) {
        logger.error(`No locators in selected text`);
        return null;
      }
      const text = selectedText.text;
      const category = this.choosenCategory;
      return {
        note: '',
        categoryId: category.id,
        category,
        highlightedQuote: { text }
      };
    },
    createAnnotation() {
      try {
        const locator = this.getSelectedLocator();
        const annotation = this.createAnnData();
        if (!locator || !annotation) {
          this.$store.dispatch('ManagePopupStore/openErrorToaster', {
            text: this.$t('PresentPublication.error.annotations')
          });
          return;
        }
        this.$store.dispatch('AnnotationsStore/processAddAnnotation', {
          annotation,
          locator,
          categoryId: this.choosenCategory.id,
          note: ''
        });
      } catch (err) {
        logger.error(`Error in CreateAnnotationPopup: ${err.stack}`);
      }
    }
  }
};
</script>
